import { GetAlertsGroupedByObjectPayload } from 'app/modules/alerts/types/objectCentricInvestigation';
import {
  AlertFiltersPayload,
  GetAlertsCachedListPayload,
  GetAlertHistogramPayload,
  RetrieveAlertEntitiesPayload,
  GetAlertsPayload,
  GetAlertHitCgdoPayload,
} from 'app/modules/alerts/requests';
import {
  GetAlertHitTransactionsPayload,
  GetTransactionsPayload,
} from 'app/modules/transactions/requests';
import { PaginationPayload } from 'app/shared/pagination/models';
import { InvestigationsPayload } from 'app/modules/investigations/types';
import { AlertSearchPayload } from 'app/modules/search/payload';
import { GetAlertHitActionEventsPayload } from 'app/modules/actionEvents/requests';

const BASE = ['alerts'] as const;
export const ALERT_QUERY_KEYS = {
  getAlert: (payload: number, useCacheEndpoint: boolean = false) => {
    if (useCacheEndpoint) {
      return [...BASE, 'alert', payload, useCacheEndpoint] as const;
    }
    return [...BASE, 'alert', payload] as const;
  },
  getAllAlerts: () => [...BASE, 'alerts'] as const,
  getAlerts: (payload: GetAlertsPayload, useCacheEndpoint: boolean = false) => {
    if (useCacheEndpoint) {
      return [
        ...ALERT_QUERY_KEYS.getAllAlerts(),
        payload,
        useCacheEndpoint,
      ] as const;
    }
    return [...ALERT_QUERY_KEYS.getAllAlerts(), payload] as const;
  },
  getAlertsFull: (
    payload: Omit<InvestigationsPayload, 'object_type' | 'short'>,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'alerts-full', payload, useCacheEndpoint] as const;
    }
    return [...BASE, 'alerts-full', payload] as const;
  },
  getAlertsCountAll: () => [...BASE, 'count'] as const,
  getAlertsCount: (
    payload: AlertFiltersPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...ALERT_QUERY_KEYS.getAlertsCountAll(),
        payload,
        useCacheEndpoint,
      ] as const;
    }
    return [...ALERT_QUERY_KEYS.getAlertsCountAll(), payload] as const;
  },

  getAlertsGroupedByObject: (payload: GetAlertsGroupedByObjectPayload) =>
    [...BASE, 'grouped', payload] as const,
  getAlertDispositionByAlertIds: (alertIds: number[]) => {
    return [
      'alert-disposition',
      'getAlertDispositionByAlertIds',
      alertIds,
    ] as const;
  },
  getAlertEntities: (
    payload: RetrieveAlertEntitiesPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'entities', payload, useCacheEndpoint] as const;
    }
    return [...BASE, 'entities', payload] as const;
  },
  getAlertTransactions: (
    id: number,
    payload?: GetTransactionsPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'transactions', id, payload, useCacheEndpoint] as const;
    }
    if (payload) {
      return [...BASE, 'transactions', id, payload] as const;
    }
    return [...BASE, 'transactions', id] as const;
  },
  getAlertInstruments: (
    id: number,
    payload?: PaginationPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'instruments', id, payload, useCacheEndpoint] as const;
    }
    if (payload) {
      return [...BASE, 'instruments', id, payload] as const;
    }
    return [...BASE, 'instruments', id] as const;
  },
  getAlertActionEvents: (
    id: number,
    payload?: PaginationPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'action-events', id, payload, useCacheEndpoint] as const;
    }
    if (payload) {
      return [...BASE, 'action-events', id, payload] as const;
    }
    return [...BASE, 'action-events', id] as const;
  },
  getAlertAmount: (id: number) => [...BASE, 'amount', id] as const,
  getAlertGraphRuleMatches: (id: number) =>
    [...BASE, 'entity-link-counts-matches', id] as const,
  getAlertPriorActivity: (id: number, useCacheEndpoint: boolean = false) => {
    if (useCacheEndpoint) {
      return [...BASE, 'prior-activity', id, useCacheEndpoint] as const;
    }
    return [...BASE, 'prior-activity', id] as const;
  },
  getHistogram: (payload: GetAlertHistogramPayload) =>
    [...BASE, 'histogram', payload] as const,
  getAlertSearch: (payload?: AlertSearchPayload) => {
    if (payload) {
      return [...BASE, 'search', payload] as const;
    }
    return [...BASE, 'search'] as const;
  },
  getAlertsCachedList: (payload: GetAlertsCachedListPayload) =>
    [...BASE, 'cached-list', payload] as const,
  getAlertHits: (alertId: number, page: number, pageSize: number) =>
    [...BASE, 'alert-hits', alertId, page, pageSize] as const,
  alertHitTxnEvents: (
    id: number,
    payload?: GetAlertHitTransactionsPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'alert-hits',
        id,
        'transaction-events',
        payload,
        useCacheEndpoint,
      ] as const;
    }
    if (payload) {
      return [
        ...BASE,
        'alert-hits',
        id,
        'transaction-events',
        payload,
      ] as const;
    }
    return [...BASE, 'alert-hits', id, 'transaction-events'] as const;
  },
  alertHitActionEvents: (
    id: number,
    payload?: GetAlertHitActionEventsPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'alert-hits',
        id,
        'action-events',
        payload,
        useCacheEndpoint,
      ] as const;
    }
    if (payload) {
      return [...BASE, 'alert-hits', id, 'action-events', payload] as const;
    }
    return [...BASE, 'alert-hits', id, 'action-events'] as const;
  },
  alertHitEntities: (
    id: number,
    payload?: GetAlertHitCgdoPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'alert-hits',
        id,
        'entities',
        payload,
        useCacheEndpoint,
      ] as const;
    }
    if (payload) {
      return [...BASE, 'alert-hits', id, 'entities', payload] as const;
    }
    return [...BASE, 'alert-hits', id, 'entities'] as const;
  },
  alertHitInstruments: (
    id: number,
    payload?: GetAlertHitCgdoPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'alert-hits',
        id,
        'instruments',
        payload,
        useCacheEndpoint,
      ] as const;
    }
    if (payload) {
      return [...BASE, 'alert-hits', id, 'instruments', payload] as const;
    }
    return [...BASE, 'alert-hits', id, 'instruments'] as const;
  },
  getAlertHitsInfinite: (alertId: number, limit: number) =>
    [...BASE, 'alert-hits-infinite', alertId, limit] as const,
  getMyAlertStats: () => [...BASE, 'my-alert-stats'] as const,
};
